import React, { useState } from 'react';

/**
 * 1:1 with NEMT template (cover + double overlay, optional glass row *ngIf=false, white card + handoff).
 * Local assets: /assets/images/branding/… — fallback to provider for cover.
 */

const PANEL_LOGO = 'https://nemtpanel.com/assets/images/nemt-logo-panel.png';
const NEMT_COVER_FALLBACK =
  'https://provider.nemtbusiness.com/assets/images/branding/nemt-cover-image.jpg';

function coverSrcPrimary() {
  return `${process.env.PUBLIC_URL || ''}/assets/images/branding/nemt-cover-image.jpg`;
}

function nemtBrandingLogoSrc() {
  return `${process.env.PUBLIC_URL || ''}/assets/images/branding/nemt-logo.png`;
}

function nemtBrandingLogoFallback() {
  return `${process.env.PUBLIC_URL || ''}/nemt_logo.png`;
}

/** `ring` in template = ring-2 in default Tailwind — keep 2px white ring. */
const RING_WHITE = 'ring-2 ring-white ring-offset-2';

function DashedPathWithArrow({ variant }) {
  const isWhite = variant === 'white';
  const line = isWhite
    ? 'h-24 w-px border-r-2 border-dashed border-white md:h-px md:w-24 md:border-b-2 md:border-r-0'
    : 'h-24 w-px border-r-2 border-dashed border-primary md:h-px md:w-24 md:border-b-2 md:border-r-0';
  const arrow = isWhite ? 'text-white' : 'text-primary';

  return (
    <div className="flex flex-col items-center md:flex-row" aria-hidden>
      <div className={line} />
      <div className="mt-0.5 md:ml-0.5 md:mt-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className={`h-6 w-6 rotate-90 md:rotate-0 ${arrow}`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
          />
        </svg>
      </div>
    </div>
  );
}

function NemtSourceRing() {
  return (
    <div
      className={`grid h-24 w-24 flex-shrink-0 place-content-center rounded-full bg-[#293462] ${RING_WHITE}`}
    >
      <div className="grid grid-cols-2">
        <img
          src={PANEL_LOGO}
          className="h-8 w-8 object-scale-down"
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
        />
        <img
          src={PANEL_LOGO}
          className="h-8 w-8 object-scale-down"
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>
      <div className="flex justify-center">
        <img
          src={PANEL_LOGO}
          className="h-8 w-8 object-scale-down"
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>
    </div>
  );
}

function CenterLockRing() {
  return (
    <div
      className="grid h-24 w-24 flex-shrink-0 place-content-center rounded-full bg-white p-6 ring-1 ring-black"
      aria-hidden
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full text-[#293462]"
        viewBox="0 0 32 32"
        aria-hidden
      >
        <path
          fill="currentColor"
          d="m11 23.18l-2-2.001l-1.411 1.41L11 26l6-6l-1.41-1.41L11 23.18zM28 30h-4v-2h4V16h-4V8a4.005 4.005 0 0 0-4-4V2a6.007 6.007 0 0 1 6 6v6h2a2.002 2.002 0 0 1 2 2v12a2.002 2.002 0 0 1-2 2z"
        />
        <path
          fill="currentColor"
          d="M20 14h-2V8A6 6 0 0 0 6 8v6H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V16a2 2 0 0 0-2-2ZM8 8a4 4 0 0 1 8 0v6H8Zm12 20H4V16h16Z"
        />
      </svg>
    </div>
  );
}

function NemtTargetRing() {
  return (
    <div
      className={`grid h-24 w-24 flex-shrink-0 place-content-center rounded-full bg-[#293462] p-6 ${RING_WHITE}`}
    >
      <img
        src={PANEL_LOGO}
        className="h-full w-full object-scale-down"
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}

function HandoffBlock({ variant }) {
  return (
    <>
      <NemtSourceRing />
      <DashedPathWithArrow variant={variant} />
      <CenterLockRing />
      <DashedPathWithArrow variant={variant} />
      <NemtTargetRing />
    </>
  );
}

export default function PortalOAuthSkeleton({ isDisablePlatform = false }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="NEMT authentication in progress"
      className="relative min-h-screen min-h-[100dvh] font-inter antialiased"
    >
      <div className="absolute inset-0">
        {/* <video class="h-full w-full object-cover" poster="/assets/videos/auth-video.mp4" allow="autoplay" loop muted playsInline><source src="/assets/videos/auth-video.mp4" type="video/mp4" /></video> */}
        {/* <img class="h-full w-full object-cover" src=".../assets/images/default/poster.png" alt="" /> */}
        <CoverImage />
        <div className="bg-is-primary/50 absolute inset-0 mix-blend-multiply" aria-hidden />
        <div className="bg-is-primary/50 absolute inset-0 mix-blend-multiply" aria-hidden />
      </div>

      <div className="relative grid min-h-screen place-content-center py-32">
        {/* *ngIf="false" in Angular — not shown; full markup + classes for parity, hidden. */}
        <div className="relative mx-auto !hidden flex max-w-6xl flex-col items-center justify-center gap-4 rounded-lg bg-white/40 p-8 sm:p-16 md:flex-row md:p-20">
          <HandoffBlock variant="white" />
        </div>

        <div className="flex w-[350px] max-w-full flex-col items-center bg-white p-8 sm:p-16 md:w-[700px] md:p-12">
          {!isDisablePlatform && (
            <div className="pb-12">
              <NemtMainLogo />
            </div>
          )}
          <div className="mx-auto flex flex-col items-center justify-center gap-4 rounded-lg md:flex-row">
            <HandoffBlock variant="primary" />
          </div>
        </div>
      </div>
    </div>
  );
}

function CoverImage() {
  const [src, setSrc] = useState(coverSrcPrimary);

  return (
    <img
      className="h-full w-full object-cover"
      src={src}
      alt=""
      onError={() => {
        if (src !== NEMT_COVER_FALLBACK) setSrc(NEMT_COVER_FALLBACK);
      }}
    />
  );
}

function NemtMainLogo() {
  const [src, setSrc] = useState(nemtBrandingLogoSrc);

  return (
    <img
      className="h-20 w-auto"
      src={src}
      alt="logo"
      onError={() => {
        if (src !== nemtBrandingLogoFallback()) setSrc(nemtBrandingLogoFallback());
      }}
    />
  );
}
